import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import mitt from 'mitt';
import Axios from 'axios';
import { marked } from 'marked';
import markedFootnote from 'marked-footnote';

import { ObserveVisibility } from 'vue-observe-visibility';
import VCalendar from 'v-calendar';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import Button from '@/components/ui/Button.vue';
import Link from '@/components/ui/Link.vue';
import 'v-calendar/style.css';
const emitter = mitt();

const renderer = (str) => {
    if (typeof str !== 'string') return '';

    marked.use(markedFootnote());
    return marked(str, { breaks: true });
}

// Custom cache implementation
class RequestCache {
    constructor(options = {}) {
        this.cache = new Map();
        this.ttl = options.ttl || 15 * 60 * 1000; // 15 minutes default
        this.maxSize = options.maxSize || 150; // Maximum cache entries
    }

    generateKey(config) {
        const { method, url, params, data } = config;
        return `${method?.toUpperCase()}_${url}_${JSON.stringify(params || {})}_${JSON.stringify(data || {})}`;
    }

    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;
        
        if (Date.now() > item.expiry) {
            this.cache.delete(key);
            return null;
        }
        
        return item.data;
    }

    set(key, data) {
        // Implement LRU eviction if cache is full
        if (this.cache.size >= this.maxSize) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }

        this.cache.set(key, {
            data,
            expiry: Date.now() + this.ttl,
            timestamp: Date.now()
        });
    }

    clear() {
        this.cache.clear();
    }

    size() {
        return this.cache.size;
    }
}

// Initialize cache
const requestCache = new RequestCache({
    ttl: 15 * 60 * 1000, // 15 minutes
    maxSize: 150 // Limit cache size
});

// Create axios instance with custom cache
const axiosInstance = Axios.create({
    baseURL: window.globals.api_url,
    withCredentials: true,
});

// Wrapper for cached requests
const ajax = {
    ...axiosInstance,
    
    async get(url, config = {}) {
        const requestConfig = { ...config, method: 'get', url };
        const cacheKey = requestCache.generateKey(requestConfig);
        
        // Check cache first
        const cachedResponse = requestCache.get(cacheKey);
        if (cachedResponse) {
            return {
                ...cachedResponse,
                config: requestConfig,
                cached: true
            };
        }
        
        // Make actual request
        try {
            const response = await axiosInstance.get(url, config);
            
            // Cache successful responses
            if (response.status >= 200 && response.status < 300) {
                requestCache.set(cacheKey, {
                    data: response.data,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers
                });
            }
            
            response.cached = false;
            return response;
        } catch (error) {
            throw error;
        }
    },
    
    // Pass through other methods without caching
    post: (url, data, config) => axiosInstance.post(url, data, config),
    put: (url, data, config) => axiosInstance.put(url, data, config),
    delete: (url, config) => axiosInstance.delete(url, config),
    patch: (url, data, config) => axiosInstance.patch(url, data, config),
    request: (config) => axiosInstance.request(config),
    
    // Expose interceptors for compatibility
    interceptors: axiosInstance.interceptors,
};

// Development mode debugging and cache management
if (process.env.NODE_ENV === 'development') {
    // Add logging interceptor
    ajax.interceptors.response.use(response => {
        const status = response.cached ? '🟢 FROM CACHE' : '🔴 FROM NETWORK';
        console.log(`${status}: ${response.config.method?.toUpperCase()} ${response.config.url}`);
        return response;
    });
    
    // Expose cache management to window for debugging
    window.cacheManager = {
        // Get cache statistics
        stats: () => ({
            size: requestCache.size(),
            maxSize: requestCache.maxSize,
            ttl: requestCache.ttl
        }),
        
        // Clear all cache
        clear: () => {
            requestCache.clear();
            console.log('🗑️ Cache cleared');
        },
        
        // View cache contents
        dump: () => {
            const entries = [];
            requestCache.cache.forEach((value, key) => {
                entries.push({
                    key,
                    expiry: new Date(value.expiry).toLocaleString(),
                    age: Date.now() - value.timestamp
                });
            });
            console.table(entries);
            return entries;
        },
        
        // Test cache with a request
        test: async (url = '/get-posts?per_page=1') => {
            console.log('🧪 Testing cache with:', url);
            
            // First request
            const start1 = performance.now();
            await ajax.get(url);
            const time1 = performance.now() - start1;
            console.log(`⏱️ First request: ${time1.toFixed(2)}ms`);
            
            // Second request (should be cached)
            const start2 = performance.now();
            await ajax.get(url);
            const time2 = performance.now() - start2;
            console.log(`⏱️ Second request: ${time2.toFixed(2)}ms`);
            
            const improvement = time1 / time2;
            console.log(`📊 Speed improvement: ${improvement.toFixed(2)}x faster`);
        }
    };
    
    console.log('🎯 Cache manager available at window.cacheManager');
    console.log('📋 Available commands: stats(), clear(), dump(), test()');
}

// Remove old cache-related code 



const isExternal = (url) => {
    // console.log(new URL(url, location.origin).origin, window.globals.home_url);
    const or = new URL(url, location.origin).origin;
    const ur = new URL(window.globals.home_url, location.origin).origin;

    // console.log(or, ur, or.indexOf(ur) === -1)

    if (process.env.NODE_ENV === 'production') {
        return or.indexOf(ur) === -1;
    } else {
        return or.indexOf('http://127.0.0.1:8080') === -1;
    }
};

const updateDocumentTitle = (title) => {
    // replace all &nbsp; with space
    title = title.replace(/&#038;/g, '&');
    document.title = window.globals.name + ' — ' + title;
};


const app = createApp(App);

app.directive('observe-visibility', {
    beforeMount: (el, binding, vnode) => {
        vnode.context = binding.instance;
        ObserveVisibility.bind(el, binding, vnode);
    },

    update: ObserveVisibility.update,
    unmounted: ObserveVisibility.unbind,
});


// router.beforeEach((to, from, next) => {
//     console.log(to, from);
//     next();
// });

app.component('svg-icon', SvgIcon);
app.component('ui-button', Button);
app.component('ui-link', Link);

app.use(VCalendar, {});
app.use(store);
app.use(router);
// app.provide('$ajax', ajax);
app.config.globalProperties.$ajax = ajax;
app.config.globalProperties.isExternal = isExternal;
app.config.globalProperties.updateDocumentTitle = updateDocumentTitle;
app.config.globalProperties.EventBus = emitter;
app.config.globalProperties.marked = renderer;
app.mount('#app');

